import { Trans, useTranslation } from "react-i18next";
import { useState, type FormEvent } from "react";
import { AlertTriangle, Bell, ChevronRight, ClipboardCheck, Star } from "lucide-react";
import type { DashboardNewsAction, DashboardNewsItem, Me } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { SearchField } from "@/components/ui/search-field";
import type { AuthSession } from "../../auth/keycloak.js";
import { formatDateTime } from "../../lib/date.js";
import { useFacilityNavParams } from "../facilities/drilldown/useFacilityNavParams.js";
import { useFacilities } from "../facilities/useFacilities.js";
import { useFacilityFavourites } from "../facilities/useFacilityFavourites.js";
import { useManualFavouritesList } from "../facilities/useManualFavouritesList.js";
import { ManualReferenceElement } from "../facilities/ManualReferenceElement.js";
import { ManualsDonut } from "./ManualsDonut.js";
import { OverviewCard } from "./OverviewCard.js";
import { useDashboardOverview } from "./useDashboardOverview.js";
import { useDashboardListStore } from "./manual-list/useDashboardListStore.js";

/** The legacy Verlauf copy: "<manual> in <facility> was set to <status>" — status label KEYS, module-level. */
export const NEWS_STATUS_KEY = {
  submit_review: "status.readyForReview",
  confirm: "status.published",
  mark: "dashboard.statusMarked",
  unmark: "dashboard.statusUnmarked",
  return_to_draft: "status.draft",
} as const satisfies Record<DashboardNewsAction, string>;

/**
 * The dashboard Übersicht (ADR-033) — the legacy's row of cards, each with an arrow to the page it summarises: Profile,
 * Manuals (the ring), Tasks (hints + warnings), History (the newest 3 status changes), Ratings (always shown — owner),
 * Favourite manuals, Favourite facilities. The numbers come from ONE aggregate read; favourites from their own reads.
 */
export function OverviewPage({ session, me }: { session: AuthSession; me: Me }) {
  const { t } = useTranslation();
  const nav = useFacilityNavParams();
  const overview = useDashboardOverview(session);
  const manualFavourites = useManualFavouritesList(session);
  const facilityFavourites = useFacilityFavourites(session);
  const facilities = useFacilities(session);
  const open = (page: Parameters<typeof nav.openView>[0]) => nav.openView(page, true);
  // The legacy Manuals card: a search box + the doughnut segments open the Manuals page PRESET (ADR-034 D5 — the
  // preset is transient client state, as the legacy's `filterOptions`; the page's store carries it across the switch).
  const preset = useDashboardListStore((s) => s.preset);
  const [term, setTerm] = useState("");
  const searchManuals = (e: FormEvent) => {
    e.preventDefault();
    preset("manuals", { name: term.trim() });
    open("manuals");
  };
  // Favourite facilities are ids (`/user/favourites`); the names come from the caller's facility tree. Both reads
  // must have SUCCEEDED before the list (or its empty state) shows — the favourites hook hands an empty set over
  // for a failed read too, which must never read as "no favourites" (panel).
  const favouriteFacilities =
    facilities.data && !facilityFavourites.isPending && !facilityFavourites.isError
      ? (() => {
          const byId = new Map(facilities.data.map((f) => [f.id, f] as const));
          return [...facilityFavourites.favourites].flatMap((id) => {
            const name = byId.get(id)?.name;
            return name === undefined ? [] : [{ id, name, permission: byId.get(id)!.permission }];
          });
        })()
      : null;
  const o = overview.data;
  const pending = overview.isError ? "—" : "…";
  return (
    <div className="flex flex-col gap-4">
      {/* Who you are + the four numbers, in ONE strip (owner 2026-11-04): Profile, Tasks and Ratings were three cards with
          two numbers each above an empty card. Every number opens the page it counts. */}
      <section
        aria-label={t("dashboard.atAGlance")}
        className="border-border/60 bg-background flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border px-5 py-4 shadow-xs dark:border-border/50"
      >
        <button
          type="button"
          onClick={() => open("profile")}
          className="focus-visible:ring-ring/50 hover:bg-muted/60 -mx-2 flex min-w-0 cursor-pointer flex-col items-start rounded-lg border-0 bg-transparent px-2 py-1 text-left outline-none focus-visible:ring-2"
        >
          <span className="text-base font-semibold">{me.displayName}</span>
          <span className="text-muted-foreground text-xs">{me.roles.length === 0 ? t("dashboard.noRoles") : me.roles.join(", ")}</span>
        </button>
        <dl className="m-0 ml-auto grid grid-cols-2 gap-2 sm:grid-cols-4 max-sm:w-full">
          <Kpi
            label={t("dashboard.warningUnit", { count: o?.tasks.warnings ?? 0 })}
            value={o ? o.tasks.warnings : pending}
            icon={<AlertTriangle aria-hidden="true" className={o && o.tasks.warnings > 0 ? "text-attention size-4" : "text-muted-foreground size-4"} />}
            onPress={() => open("tasks")}
          />
          <Kpi
            label={t("dashboard.hintUnit", { count: o?.tasks.hints ?? 0 })}
            value={o ? o.tasks.hints : pending}
            icon={<Bell aria-hidden="true" className="text-muted-foreground size-4" />}
            onPress={() => open("tasks")}
          />
          <Kpi
            label={t("status.readyForReview")}
            value={o ? o.manuals.review : pending}
            icon={<ClipboardCheck aria-hidden="true" className="text-muted-foreground size-4" />}
            onPress={() => {
              preset("manuals", { statuses: ["review"] });
              open("manuals");
            }}
          />
          <Kpi
            label={o?.ratings ? t("dashboard.averageOfRatings", { count: o.ratings.count }) : t("dashboard.noRatingsYet")}
            value={o ? (o.ratings ? o.ratings.average.toFixed(1) : "—") : pending}
            icon={<Star aria-hidden="true" className="text-attention size-4" />}
            onPress={() => open("ratings")}
          />
        </dl>
      </section>
    {/* Columns, not a grid row: a card no longer stretches to its tallest neighbour, and the next card moves up instead of
        leaving the hole (design pass 2026-11-04). Reading order runs down each column. */}
    <div className="columns-1 gap-4 md:columns-2 [&>*]:mb-4 [&>*]:break-inside-avoid">

      <OverviewCard page="manuals" title={t("manuals.raw")} onOpen={open}>
        {overview.isPending ? (
          <p role="status" className="text-muted-foreground text-sm">{t("dashboard.loadingEllipsis")}</p>
        ) : overview.isError ? (
          <div className="flex flex-col items-start gap-2">
            <p role="alert" className="text-destructive text-sm">{t("dashboard.couldntLoadTheOverview")}</p>
            <Button variant="outline" size="sm" onPress={() => void overview.refetch()}>{t("dashboard.tryAgain")}</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <form onSubmit={searchManuals} aria-label={t("dashboard.searchManuals")}>
              {/* Enter submits the surrounding <form> natively (searchManuals); no second search button beside the field (owner 2026-11-04). */}
              <SearchField
                label={t("dashboard.searchManualsByName")}
                placeholder={t("dashboard.searchManuals2")}
                value={term}
                onChange={setTerm}
                clearLabel={t("manualManagement.toolbar.clearSearch")}
                className="w-full"
              />
            </form>
            <ManualsDonut
              manuals={o!.manuals}
              onSelect={(status) => {
                preset("manuals", { statuses: [status] });
                open("manuals");
              }}
            />
          </div>
        )}
      </OverviewCard>

      <OverviewCard page="history" title={t("dashboard.history")} onOpen={open}>
        {o ? (
          o.news.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("dashboard.noNewsYet")}</p>
          ) : (
            <ul aria-label={t("dashboard.latestStatusChanges")} className="m-0 flex list-none flex-col gap-2 p-0">
              {o.news.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => nav.openManualDetails(n.facilityId, n.manualId)}
                    className="bg-muted/50 hover:bg-muted focus-visible:ring-ring/50 w-full rounded-md border-0 px-3 py-2 text-left text-xs outline-none focus-visible:ring-2"
                  >
                    <span className="block">
                      <NewsLine n={n} />
                    </span>
                    <span className="text-muted-foreground mt-1 block">{formatDateTime(n.createdAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : (
          <p className="text-muted-foreground text-sm">{overview.isError ? "—" : t("other.loading")}</p>
        )}
      </OverviewCard>

      <OverviewCard page="favourites" title={t("dashboard.favouriteManuals")} onOpen={open}>
        {manualFavourites.data ? (
          manualFavourites.data.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("dashboard.noFavourites")}</p>
          ) : (
            <ul aria-label={t("dashboard.favouriteManuals")} className="m-0 flex list-none flex-col gap-2 p-0">
              {manualFavourites.data.slice(0, 3).map((m) => (
                <li key={m.id}>
                  <ManualReferenceElement manual={m} path={m.facilityName} onOpen={nav.openManualDetails} />
                </li>
              ))}
            </ul>
          )
        ) : (
          <p className="text-muted-foreground text-sm">{manualFavourites.isError ? "—" : t("other.loading")}</p>
        )}
      </OverviewCard>

      <OverviewCard page="favourite-facilities" title={t("dashboard.favouriteFacilities")} onOpen={open}>
        {favouriteFacilities ? (
          favouriteFacilities.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("dashboard.noFavourites")}</p>
          ) : (
            <ul aria-label={t("dashboard.favouriteFacilities")} className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
              {/* keyed by id — facility names repeat across the tree (line numbers under different parents) */}
              {favouriteFacilities.slice(0, 3).map((f) => (
                <li key={f.id}>
                  {/* Opens the area, as on the favourite-areas page: its manuals, or its sub-areas without a manuals grant. */}
                  <button
                    type="button"
                    onClick={() => (f.permission === null ? nav.drillTo(f.id) : nav.openManuals(f.id))}
                    className="hover:bg-muted/60 focus-visible:ring-ring/50 flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border-0 bg-transparent px-2 py-1.5 text-left outline-none focus-visible:ring-2"
                  >
                    <span className="truncate">{f.name}</span>
                    <ChevronRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : (
          <p className="text-muted-foreground text-sm">{facilities.isError || facilityFavourites.isError ? "—" : t("other.loading")}</p>
        )}
      </OverviewCard>
    </div>
    </div>
  );
}

/** One number of the strip: a button to the page it counts. */
function Kpi({ label, value, icon, onPress }: { label: string; value: number | string; icon: React.ReactNode; onPress: () => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <dt className="sr-only">{label}</dt>
      <dd className="m-0">
        <button
          type="button"
          onClick={onPress}
          aria-label={t("dashboard.kpiLabel", { n: String(value), label })}
          className="hover:bg-muted/60 focus-visible:ring-ring/50 flex w-full min-w-28 cursor-pointer flex-col items-start rounded-lg border-0 bg-transparent px-3 py-1.5 text-left outline-none focus-visible:ring-2"
        >
          <span className="flex items-center gap-1.5 text-2xl font-semibold tabular-nums">
            {value}
            {icon}
          </span>
          <span aria-hidden="true" className="text-muted-foreground text-xs">{label}</span>
        </button>
      </dd>
    </div>
  );
}

/**
 * One Verlauf line, shared in shape with `HistoryPage`'s: the three names are component CHILDREN rather than
 * interpolated values, because `Trans` re-parses an interpolated string as markup under `escapeValue: false`.
 */
function NewsLine({ n }: { n: DashboardNewsItem }) {
  const { t } = useTranslation();
  const parts = {
    manual: <strong>{n.manualName}</strong>,
    facility: <strong>{n.facilityName}</strong>,
    status: <strong>{t(NEWS_STATUS_KEY[n.action])}</strong>,
  };
  return (
    <>
      {n.actorName ? (
        <Trans i18nKey="dashboard.newsSentenceBy" components={parts} values={{ actor: n.actorName }} />
      ) : (
        <Trans i18nKey="dashboard.newsSentence" components={parts} />
      )}
      .
    </>
  );
}
